import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { value: "startup", label: "Startup / SMB" },
  { value: "scaleup", label: "Scale-up" },
  { value: "review", label: "One-off GTM Review" },
];

export const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    stage: "startup",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const upd = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        stage: form.stage,
        message: form.message.trim(),
      });
      setSubmitted(true);
      toast.success("Inquiry received. We'll be in touch within 24 hours.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="contact-success"
        className="border border-violet-500/30 bg-violet-500/5 p-10 text-center"
      >
        <CheckCircle2 className="h-8 w-8 text-violet-300 mx-auto" strokeWidth={1.5} />
        <div className="mt-6 font-display text-2xl text-zinc-50">
          Signal received.
        </div>
        <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
          A partner will review your context and reply within one business day.
          In the meantime, expect a short brief — not a boilerplate calendar
          link.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      className="grid grid-cols-1 md:grid-cols-2 gap-5"
      noValidate
    >
      <Field label="Name" required>
        <input
          data-testid="contact-input-name"
          value={form.name}
          onChange={upd("name")}
          className={inputCls}
          placeholder="Jane Chen"
          required
        />
      </Field>
      <Field label="Work Email" required>
        <input
          data-testid="contact-input-email"
          type="email"
          value={form.email}
          onChange={upd("email")}
          className={inputCls}
          placeholder="jane@company.com"
          required
        />
      </Field>
      <Field label="Company">
        <input
          data-testid="contact-input-company"
          value={form.company}
          onChange={upd("company")}
          className={inputCls}
          placeholder="Company Inc."
        />
      </Field>
      <Field label="Business Stage" required>
        <Select value={form.stage} onValueChange={upd("stage")}>
          <SelectTrigger
            data-testid="contact-select-stage"
            className="rounded-none bg-zinc-950/60 border-white/10 text-zinc-50 h-11 focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10 rounded-none">
            {STAGES.map((s) => (
              <SelectItem
                key={s.value}
                value={s.value}
                data-testid={`contact-stage-${s.value}`}
                className="text-zinc-100 focus:bg-violet-500/15 focus:text-zinc-50 rounded-none"
              >
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <div className="md:col-span-2">
        <Field label="What do you want to move?" required>
          <textarea
            data-testid="contact-input-message"
            value={form.message}
            onChange={upd("message")}
            rows={5}
            className={`${inputCls} resize-none py-3`}
            placeholder="A few lines on your current motion, where it stalls, and what a great outcome looks like in 90 days."
            required
          />
        </Field>
      </div>
      <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
        <p className="text-xs text-zinc-500 leading-relaxed max-w-md">
          By submitting, you agree to be contacted by Catlyz. We never share
          your details, and we never send spam.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="contact-submit-button"
          className="inline-flex items-center gap-2 bg-zinc-50 text-zinc-950 px-6 py-3 text-sm font-medium hover:bg-white disabled:opacity-60 transition-all"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ArrowRight className="h-4 w-4" />
          )}
          {submitting ? "Sending" : "Book an Assessment"}
        </button>
      </div>
    </form>
  );
};

const inputCls =
  "w-full h-11 bg-zinc-950/60 border border-white/10 text-zinc-50 px-3 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] font-mono-ui uppercase tracking-[0.22em] text-zinc-500 mb-2">
      {label} {required && <span className="text-violet-400">*</span>}
    </span>
    {children}
  </label>
);
